import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { MagnifyingGlass, DownloadSimple, FunnelSimple } from "@phosphor-icons/react";

const SEV = {
  critical: "bg-[#FF3B30]/15 text-[#FF3B30] border-[#FF3B30]/40",
  high: "bg-[#FF9F0A]/15 text-[#FF9F0A] border-[#FF9F0A]/40",
  medium: "bg-[#0A84FF]/15 text-[#0A84FF] border-[#0A84FF]/40",
  low: "bg-[#71717A]/15 text-[#A1A1AA] border-[#71717A]/40",
};

export default function Incidents() {
  const [incidents, setIncidents] = useState([]);
  const [q, setQ] = useState("");
  const [type, setType] = useState("all");
  const [sev, setSev] = useState("all");
  const [status, setStatus] = useState("all");

  useEffect(() => { api.get("/incidents").then((r) => setIncidents(r.data)); }, []);

  const types = useMemo(() => ["all", ...Array.from(new Set(incidents.map((i) => i.type)))], [incidents]);
  const statuses = useMemo(() => ["all", ...Array.from(new Set(incidents.map((i) => i.status)))], [incidents]);

  const filtered = useMemo(() => incidents.filter((i) => {
    if (type !== "all" && i.type !== type) return false;
    if (sev !== "all" && i.severity !== sev) return false;
    if (status !== "all" && i.status !== status) return false;
    if (q && !`${i.id} ${i.type} ${i.camera}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  }), [incidents, type, sev, status, q]);

  const exportCsv = () => {
    const rows = [["Incident ID", "Type", "Severity", "Camera", "Confidence", "Status", "Timestamp"]];
    filtered.forEach((i) => rows.push([i.id, i.type, i.severity, i.camera, `${i.confidence}%`, i.status, new Date(i.ts).toLocaleString()]));
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = `sentryvision_incidents_${Date.now()}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const Select = ({ value, onChange, options, testid }) => (
    <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
      className="bg-[#1A1A1E] border border-white/10 px-3 py-2 text-sm outline-none focus:border-[#007AFF] transition-colors">
      {options.map((o) => <option key={o} value={o}>{o === "all" ? "All" : o}</option>)}
    </select>
  );

  return (
    <div className="space-y-4">
      <div className="bg-[#121214] border border-white/10 p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 bg-[#1A1A1E] border border-white/10 px-3 py-2 flex-1 min-w-[200px]">
            <MagnifyingGlass size={16} className="text-[#71717A]" />
            <input data-testid="incident-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search ID, type, camera…"
              className="bg-transparent text-sm outline-none w-full placeholder:text-[#71717A]" />
          </div>
          <div className="flex items-center gap-1 text-xs uppercase tracking-widest text-[#71717A]"><FunnelSimple size={14} /> Filter</div>
          <Select value={type} onChange={setType} options={types} testid="filter-type" />
          <Select value={sev} onChange={setSev} options={["all", "critical", "high", "medium", "low"]} testid="filter-severity" />
          <Select value={status} onChange={setStatus} options={statuses} testid="filter-status" />
          <button data-testid="export-csv" onClick={exportCsv}
            className="flex items-center gap-1.5 bg-[#007AFF] hover:bg-[#3395FF] text-white px-3 py-2 text-sm font-display font-bold uppercase tracking-wide transition-colors">
            <DownloadSimple size={16} weight="bold" /> Export CSV
          </button>
        </div>
        <p className="font-mono-data text-xs text-[#71717A] mt-3">{filtered.length} of {incidents.length} incidents</p>
      </div>

      <div className="bg-[#121214] border border-white/10 overflow-x-auto">
        <table className="w-full text-sm" data-testid="incidents-table">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-widest text-[#71717A] border-b border-white/10">
              <th className="px-4 py-2.5 font-medium">Incident ID</th>
              <th className="px-4 py-2.5 font-medium">Type</th>
              <th className="px-4 py-2.5 font-medium">Severity</th>
              <th className="px-4 py-2.5 font-medium">Camera</th>
              <th className="px-4 py-2.5 font-medium">Confidence</th>
              <th className="px-4 py-2.5 font-medium">Status</th>
              <th className="px-4 py-2.5 font-medium">Timestamp</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((i) => (
              <tr key={i.id} data-testid={`incident-row-${i.id}`} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                <td className="px-4 py-3 font-mono-data text-[#3395FF]">{i.id}</td>
                <td className="px-4 py-3">{i.type}</td>
                <td className="px-4 py-3"><span className={`font-mono-data text-[10px] px-1.5 py-0.5 border uppercase ${SEV[i.severity]}`}>{i.severity}</span></td>
                <td className="px-4 py-3 text-[#A1A1AA]">{i.camera}</td>
                <td className="px-4 py-3 font-mono-data">{i.confidence}%</td>
                <td className="px-4 py-3 font-mono-data text-[#A1A1AA]">{i.status}</td>
                <td className="px-4 py-3 font-mono-data text-[#71717A] text-xs">{new Date(i.ts).toLocaleString()}</td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-[#71717A] font-mono-data text-sm">No incidents match your filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
